import { useEffect, useContext, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import FormPreview from '../../components/preview/FormPreview.js';
import { AssessmentContext } from '../../context/AssessmentContext.js';
import useSessionStorage from '../../hooks/useSessionStorage';
import { customFetch } from '../../scripts/common/customFetch';
import CustomDialog from '../../components/ui/feedback/CustomDialog.js';
import { useTranslation } from 'react-i18next';

const AuthorForm = () => {
  const [loading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState();
  const { updateLearnosityRequestObject, updatePnp } = useContext(AssessmentContext);
  const { clearStorage, setStorageItem, getStorageItem, removeStorageItem } =
    useSessionStorage();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogProps, setDialogProps] = useState();
  const { t } = useTranslation();

  const loginApiCall = useCallback(
    async (setErrorDialogProps, setOpenErrorDialog) => {
      const { data, errorObj } = await customFetch({
        url: `${window.env.REACT_APP_AUTHORFORM_LOGIN_URL}?authorId=${formData.authorId}&formId=${formData.formId}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + formData.accessToken,
        },
      });
      if (data) {
        const signedObject =
          data?.learnositySignedPayload &&
          JSON.parse(data?.learnositySignedPayload);
        if (signedObject) {
          updateLearnosityRequestObject(
            signedObject,
            updateLearnosityRequestObject
          );
        }
        if(data?.pnp) {
          updatePnp(data.pnp);
        }
        if (data?.formInfo) {
          setStorageItem(Storage.STORAGE_FORM_INFO, data?.formInfo);
        }
        if (data?.pwaConfig) {
          setStorageItem(Storage.STORAGE_PWA_CONFIG, data?.pwaConfig);
        }
        setOpenErrorDialog(false);
        setErrorDialogProps();
      }
      if (errorObj) {
        clearStorage();
        if (Math.floor(errorObj.status / 100) === 5) {
          setErrorDialogProps({
            title: t('common.error'),
            message: t('pages.authorForm.errorFetchingForm'),
          });
        } else {
          setErrorDialogProps({
            title: t('components.common.accessDenied'),
            message: t('pages.authorForm.formNotAvailable'),
          });
        }

        setOpenErrorDialog(true);
      }
      setIsLoading(false);
    },
    [formData, clearStorage, setIsLoading, updateLearnosityRequestObject, t]
  );

  const messageEventHandler = useCallback(
    (event) => {
      if (
        event.origin === window.env.REACT_APP_WAYPOINT_AUTHOR_URL &&
        event.data?.accessToken
      ) {
        removeStorageItem('waitingForAccessToken');
        const authorId = searchParams?.get('authorId');
        const formId = searchParams?.get('formId');

        if (!authorId || !formId) {
          setDialogProps({
            title: t('components.common.accessDenied'),
            message: t('pages.authorForm.invalidRequest'),
          });
          setOpenDialog(true);
        } else {
          const authorFormData = {
            authorId: authorId,
            formId: formId,
            accessToken: event.data.accessToken,
          };
          setFormData(authorFormData);
        }
      }
    },
    [searchParams, removeStorageItem, t]
  );

  const noAccessTokenReceivedTimeout = useCallback(() => {
    let waitingForAccessToken = getStorageItem('waitingForAccessToken');
    if (waitingForAccessToken) {
      setDialogProps({
        title: t('pages.authorForm.unableToVerifyAccess'),
        message: t('pages.authorForm.closeWindowMessage'),
      });
      setOpenDialog(true);
    }
  }, [getStorageItem, setOpenDialog, setDialogProps, t]);

  useEffect(() => {
    updateLearnosityRequestObject(); //need to always clear request, always use recent passed access token
    setIsLoading(true);
    setStorageItem('waitingForAccessToken', true);
    window.addEventListener('message', messageEventHandler);
    window.opener?.postMessage('getAccessToken', '*');
    setTimeout(noAccessTokenReceivedTimeout, 2000); //wait for 2 seconds to receive access token from authoring

    return () => {
      window.removeEventListener('message', messageEventHandler);
    };
  }, [
    messageEventHandler,
    setIsLoading,
    setStorageItem,
    noAccessTokenReceivedTimeout,
    updateLearnosityRequestObject
  ]);

  return (
    <>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <CustomDialog open={openDialog} dialogProps={dialogProps} />
      {formData && <FormPreview loginApiCall={loginApiCall} />}
    </>
  );
};
export default AuthorForm;
